import { formatDate, formatDateTime, formatPhone, formatPlate, formatQuantity, t } from "@hakili/i18n";
import { AlertTriangle, CheckCircle2, MapPin, Phone } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * `round C5.0`, `C5.0.1`: **the cabinet's page behind the link.**
 *
 * The tour sheet leaves the console as a no-login link (`CN-A`, WF-CON-32
 * step 1) and this is what stands behind it: the sheet as the drawing draws it
 * for the cabinet (`hakili-expert.jsx`, `FeuilleTournee`, the « Étape 1 · le
 * lien » surface) , the cabinet's name, the Thursday, the version, the stops
 * in the tour's order with their vehicles, exactly the list that left.
 *
 * **The acknowledgement is posted at the OPENING of the link**, which is the
 * record's text (WF-CON-32 step 5: *« In production the acknowledgement is
 * posted automatically when the cabinet opens its link »*) and the drawing's
 * sentence (*« Réception confirmée à l'ouverture de ce lien »*). The orders of
 * `C5.0` named a control « Accuser réception »; the record's text wins and the
 * divergence is flagged in the addendum. The page posts the VERSION it shows,
 * and the door refuses one that is not the link's.
 *
 * **The token is a credential.** It is read from the path and handed to the
 * two doors, and printed nowhere on the page , not in a title, not in a link,
 * not in a data attribute. The document carries `Referrer-Policy: no-referrer`
 * so the path never leaves with a click, and loads no third-party script.
 *
 * `SheetPage` is PURE , it fetches nothing and decides nothing , so the unit
 * spec can stand it up in each of its states; `TourSheetScreen` around it
 * reads and acknowledges by the two doors.
 *
 * `round C5.1`, `C5.1.1`: **the sheet's facts, from the VERSION.** The door
 * answers `stops` as they stood when the sheet left , the seller, the lot's
 * address, the on-site contact and phone, each vehicle's model, plate,
 * mileage and colour , and, per vehicle, whether expertise holds a report
 * NOW (the chip and the « N sur M » of the drawing). Every fact not there is
 * a dash (`R81`): a version older than `C5.1` carries no stops and the page
 * falls back to the list it always had; the phone with no number prints its
 * dash and no `tel:` link. The HONORAIRES are a dash on every tile and line:
 * the fee follows the founder's grid, not yet given.
 */

/** What `GET /api/v1/public/tour-sheets/{token}` answers under `sheet`. */
export interface SheetAnswer {
  readonly version: number;
  readonly dayLabel: string;
  readonly cabinetName: string;
  readonly sentAt: string;
  readonly vehicles: readonly {
    readonly reference: string;
    readonly vehicleLabel: string;
    readonly vehiclePlate: string | null;
    readonly sellerName: string;
    readonly clientName: string | null;
  }[];
  readonly acknowledgedAt: string | null;
  /** `C5.1.0`: the version's facts as they stood at send; `[]` before `C5.1`. */
  readonly stops: readonly SheetStopAnswer[];
}

export interface SheetStopAnswer {
  readonly sellerName: string;
  readonly address: string | null;
  readonly contactName: string | null;
  readonly contactPhone: string | null;
  readonly vehicles: readonly {
    readonly reference: string;
    readonly demandId?: string;
    readonly stockReference: string;
    readonly vehicleLabel: string;
    readonly vehiclePlate: string | null;
    readonly mileageKm: number;
    readonly colour: string;
    readonly clientName: string | null;
    /** expertise holds a report for this demand, read at the opening. */
    readonly reported: boolean;
    /** `C5-2`, decision D: the pass failed, the demand back on the queue with this reason. */
    readonly preventedReason?: string | null;
  }[];
}

/**
 * `round C5-2`, `M2`: what the SPACE adds around the sheet , the fee on the
 * responsable's desk (`OD-32`: the field expert's phone and the public link
 * carry none), the sentence for the chair, and the vehicle's own control.
 */
export interface SpaceProps {
  readonly chair: "expert" | "responsable";
  /** The cabinet's own figure per vehicle (`OD-29`); `null` until entered, a dash. Never on the field's phone. */
  readonly feePerVehicle?: number | null;
  readonly onVehicle?: (vehicle: { readonly reference: string; readonly demandId: string | null }) => void;
}

/** The page's states, derived from the two doors' answers. */
export type SheetState =
  | { readonly kind: "loading" }
  | { readonly kind: "sheet"; readonly sheet: SheetAnswer; readonly acknowledgedAt: string | null }
  /** The door refused the link in words , superseded, expired, unknown. */
  | { readonly kind: "refused"; readonly message: string };

const DASH = t("common.valeur.tiret");

/** One vehicle line as the page draws it; a fact not carried is `null`, printed as a dash. */
interface Line {
  readonly reference: string;
  readonly demandId?: string;
  readonly vehicleLabel: string;
  readonly vehiclePlate: string | null;
  readonly mileageKm: number | null;
  readonly colour: string | null;
  readonly clientName: string | null;
  /** `null` when the version carries no facts: the chip is not drawn on a guess. */
  readonly reported: boolean | null;
  readonly preventedReason?: string | null;
}

interface Stop {
  readonly sellerName: string;
  readonly address: string | null;
  readonly contactName: string | null;
  readonly contactPhone: string | null;
  readonly vehicles: readonly Line[];
}

/**
 * The stops the page draws: the VERSION's when it carries them (`C5.1`), else
 * one per seller from the list, every fact a dash , a sheet sent before
 * `C5.1` still opens, and never invents an address.
 */
const stopsOf = (sheet: SheetAnswer): readonly Stop[] => {
  if (sheet.stops.length > 0) return sheet.stops;
  const stops: { sellerName: string; vehicles: Line[] }[] = [];
  for (const vehicle of sheet.vehicles) {
    const line: Line = { ...vehicle, mileageKm: null, colour: null, reported: null };
    const last = stops.at(-1);
    if (last?.sellerName === vehicle.sellerName) last.vehicles.push(line);
    else stops.push({ sellerName: vehicle.sellerName, vehicles: [line] });
  }
  return stops.map((stop) => ({ ...stop, address: null, contactName: null, contactPhone: null }));
};

/** The drawing's chip for a vehicle: « À contrôler » until expertise holds its report; « Empêché » when the pass failed. */
const chipOf = (reported: boolean, prevented: boolean): { readonly label: string; readonly shade: string } =>
  prevented
    ? { label: t("expert.feuille.chipEmpeche"), shade: "bg-amber-50 text-amber-700" }
    : reported
      ? { label: t("expert.feuille.chipEnvoye"), shade: "bg-emerald-50 text-emerald-700" }
      : { label: t("expert.feuille.chipAFaire"), shade: "border border-gray-200 bg-white text-gray-500" };

const fill = (key: Parameters<typeof t>[0], values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, value), t(key));

/** The sheet, to the drawing. Pure. */
export function SheetPage(props: {
  readonly state: SheetState;
  readonly space?: SpaceProps;
}): React.JSX.Element {
  const { state, space } = props;
  if (state.kind === "loading") {
    return (
      <div className="px-5 pt-5">
        <p className="text-sm text-gray-500">{t("common.state.chargement")}</p>
      </div>
    );
  }
  if (state.kind === "refused") {
    // The door's own sentence , « Cette feuille a été remplacée. », « Ce lien
    // a expiré. », « Ce lien ne correspond à aucune feuille de tournée. » ,
    // never a stack trace.
    return (
      <div className="px-5 pt-5">
        <h1 className="text-xl font-semibold tracking-tight">{t("expert.feuille.titre")}</h1>
        <div
          role="alert"
          data-testid="feuille-refus"
          className="mt-3 flex items-start gap-2.5 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div>
            <p className="text-sm font-semibold text-gray-900">{state.message}</p>
            <p className="mt-1 text-xs leading-relaxed text-gray-700">{t("expert.feuille.refusAide")}</p>
          </div>
        </div>
      </div>
    );
  }
  const { sheet, acknowledgedAt } = state;
  const stops = stopsOf(sheet);
  const lines = stops.flatMap((stop) => stop.vehicles);
  const reported = lines.filter((line) => line.reported === true).length;
  const prevented = lines.filter((line) => (line.preventedReason ?? null) !== null).length;
  // « À contrôler » counts what expertise has no report for; a version
  // without facts counts nothing it cannot know.
  const remaining = sheet.stops.length > 0 ? lines.length - reported - prevented : null;
  // `OD-32`: the fee is the responsable's desk's alone; the link and the
  // field expert's phone carry the drawing's third tile, « Empêchés ».
  const fees = space?.chair === "responsable" ? (space.feePerVehicle ?? null) : undefined;
  const feeOf = (): string => (fees === null || fees === undefined ? DASH : formatQuantity(fees));
  const tourFee = (): string =>
    fees === null || fees === undefined ? DASH : formatQuantity(fees * (lines.length - prevented));
  return (
    <div className="pb-24">
      <div className="px-5 pt-5 pb-3">
        <h1 className="text-xl font-semibold tracking-tight">{t("expert.feuille.titre")}</h1>
        <p data-testid="feuille-entete" className="mt-1 text-sm text-gray-500">
          {sheet.cabinetName} · {sheet.dayLabel}
        </p>
      </div>

      {/* The acknowledgement: a fact the console waits for, posted at the
          opening, carrying the version it covers. */}
      <div className="mb-3 px-5">
        {acknowledgedAt === null ? (
          <div
            data-testid="feuille-accuse-attente"
            className="flex items-start gap-2.5 rounded-2xl border border-gray-200 bg-white px-4 py-3"
          >
            <p className="text-xs leading-relaxed text-gray-700">
              {fill("expert.feuille.versionEnvoyee", {
                version: String(sheet.version),
                date: formatDate(new Date(sheet.sentAt)),
              })}{" "}
              {t("expert.feuille.receptionEnCours")}
            </p>
          </div>
        ) : (
          <div
            data-testid="feuille-accuse"
            className="flex items-start gap-2.5 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3"
          >
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            <p className="text-xs leading-relaxed text-gray-700">
              {fill("expert.feuille.versionEnvoyee", {
                version: String(sheet.version),
                date: formatDate(new Date(sheet.sentAt)),
              })}{" "}
              {fill("expert.feuille.receptionConfirmee", { date: formatDateTime(new Date(acknowledgedAt)) })}{" "}
              {t("expert.feuille.sansConfirmation")}
            </p>
          </div>
        )}
      </div>

      <div className="mb-4 px-5">
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-xs text-gray-500">{t("expert.feuille.vehicules")}</p>
              <p data-testid="feuille-total" className="mt-0.5 text-xl font-semibold tabular-nums">
                {lines.length}
              </p>
              <p className="mt-0.5 text-xs text-gray-400">
                {fill(stops.length > 1 ? "expert.feuille.arrets" : "expert.feuille.arret", {
                  n: String(stops.length),
                })}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">{t("expert.feuille.aControler")}</p>
              <p data-testid="feuille-restants" className="mt-0.5 text-xl font-semibold tabular-nums">
                {remaining ?? DASH}
              </p>
              <p className="mt-0.5 text-xs text-gray-400">
                {fill(reported > 1 ? "expert.feuille.nEnvoyes" : "expert.feuille.nEnvoye", {
                  n: String(reported),
                })}
              </p>
            </div>
            {fees === undefined ? (
              <div>
                <p className="text-xs text-gray-500">{t("expert.feuille.empeches")}</p>
                <p data-testid="feuille-empeches" className="mt-0.5 text-xl font-semibold tabular-nums">
                  {prevented}
                </p>
                <p className="mt-0.5 text-xs text-gray-400">
                  {prevented > 0 ? t("expert.feuille.empechesSignales") : t("expert.feuille.empechesAucun")}
                </p>
              </div>
            ) : (
              <div>
                {/* The cabinet's own figure (`OD-29`), on the responsable's desk alone. */}
                <p className="text-xs text-gray-500">{t("expert.feuille.honoraires")}</p>
                <p
                  data-testid="feuille-honoraires"
                  className="mt-0.5 text-xl font-semibold text-indigo-600 tabular-nums"
                >
                  {tourFee()}
                </p>
                <p className="mt-0.5 text-xs text-gray-400">{t("expert.feuille.honorairesTournee")}</p>
              </div>
            )}
          </div>
          <p data-testid="feuille-version" className="mt-2 text-xs text-gray-400">
            {fill("expert.feuille.versionDu", {
              version: String(sheet.version),
              date: formatDate(new Date(sheet.sentAt)),
            })}
          </p>
        </div>
      </div>

      {stops.map((stop, rank) => (
        <div key={stop.sellerName} className="mb-4 px-5" data-testid={`feuille-arret-${String(rank + 1)}`}>
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold">
              {fill("expert.feuille.arretTitre", { n: String(rank + 1), seller: stop.sellerName })}
            </h2>
            <span
              data-testid={`feuille-arret-${String(rank + 1)}-faits`}
              className="text-xs text-gray-400 tabular-nums"
            >
              {sheet.stops.length > 0
                ? fill("expert.feuille.faitsSur", {
                    n: String(stop.vehicles.filter((line) => line.reported === true).length),
                    m: String(stop.vehicles.length),
                  })
                : fill(stop.vehicles.length > 1 ? "expert.feuille.nVehicules" : "expert.feuille.nVehicule", {
                    n: String(stop.vehicles.length),
                  })}
            </span>
          </div>
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-4 py-3">
              <div className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                <div className="flex-1">
                  {/* The lot's address from catalog and the responsable from
                      identity, as they stood at send; absent, a dash (`R81`),
                      never a guess. */}
                  <p
                    data-testid={`feuille-arret-${String(rank + 1)}-adresse`}
                    className="text-sm leading-snug text-gray-700"
                  >
                    {stop.address ?? DASH}
                  </p>
                  <p
                    data-testid={`feuille-arret-${String(rank + 1)}-contact`}
                    className="mt-0.5 text-xs text-gray-500"
                  >
                    {t("expert.feuille.surPlace")} {stop.contactName ?? DASH}
                  </p>
                </div>
              </div>
              {/* « Appeler » is a phone link when identity holds the number,
                  the drawing's control greyed with its dash when it does not. */}
              {stop.contactPhone === null ? (
                <span
                  data-testid={`feuille-arret-${String(rank + 1)}-appeler`}
                  aria-disabled="true"
                  title={t("expert.feuille.appelerAbsent")}
                  className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white py-2.5 text-xs font-medium text-gray-400"
                >
                  <Phone className="h-3.5 w-3.5 text-gray-300" />
                  {t("expert.feuille.appeler")} {DASH}
                </span>
              ) : (
                <a
                  data-testid={`feuille-arret-${String(rank + 1)}-appeler`}
                  href={`tel:${stop.contactPhone}`}
                  className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white py-2.5 text-xs font-medium text-gray-700"
                >
                  <Phone className="h-3.5 w-3.5 text-gray-400" />
                  {t("expert.feuille.appeler")} {formatPhone(stop.contactPhone)}
                </a>
              )}
            </div>
            <ul className="divide-y divide-gray-50">
              {stop.vehicles.map((vehicle) => (
                <li
                  key={vehicle.reference}
                  data-testid={`feuille-vehicule-${vehicle.reference}`}
                  className="px-4 py-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold leading-snug text-gray-900">
                        {vehicle.vehicleLabel}
                      </p>
                      <p className="text-xs text-gray-400 tabular-nums">
                        {vehicle.reference} ·{" "}
                        {vehicle.vehiclePlate === null ? DASH : formatPlate(vehicle.vehiclePlate)}
                      </p>
                    </div>
                    {fees === undefined ? null : (
                      <div
                        className="shrink-0 text-right"
                        data-testid={`feuille-vehicule-${vehicle.reference}-honoraires`}
                      >
                        <p className="text-sm font-semibold leading-none tabular-nums">{feeOf()}</p>
                        <p className="mt-0.5 text-[10px] text-gray-400">{t("common.monnaie.fcfa")}</p>
                      </div>
                    )}
                  </div>
                  <p
                    data-testid={`feuille-vehicule-${vehicle.reference}-faits`}
                    className="mt-1 text-xs text-gray-500 tabular-nums"
                  >
                    {vehicle.mileageKm === null
                      ? DASH
                      : fill("expert.feuille.km", { n: formatQuantity(vehicle.mileageKm) })}{" "}
                    · {vehicle.colour ?? DASH}
                  </p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    {vehicle.reported !== null && (
                      <span
                        data-testid={`feuille-vehicule-${vehicle.reference}-chip`}
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${chipOf(vehicle.reported, (vehicle.preventedReason ?? null) !== null).shade}`}
                      >
                        {chipOf(vehicle.reported, (vehicle.preventedReason ?? null) !== null).label}
                      </span>
                    )}
                    {space?.onVehicle !== undefined &&
                    vehicle.reported === false &&
                    (vehicle.preventedReason ?? null) === null ? (
                      <button
                        type="button"
                        data-testid={`feuille-vehicule-${vehicle.reference}-ouvrir`}
                        onClick={() => {
                          space.onVehicle?.({
                            reference: vehicle.reference,
                            demandId: vehicle.demandId ?? null,
                          });
                        }}
                        className="rounded-full border border-indigo-200 bg-white px-2 py-0.5 text-xs font-medium text-indigo-700"
                      >
                        {t("expert.feuille.ouvrir")}
                      </button>
                    ) : null}
                    {vehicle.clientName === null ? (
                      <span className="text-xs text-gray-400">{t("expert.feuille.controleStock")}</span>
                    ) : (
                      <span className="text-xs text-gray-500">{vehicle.clientName}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}

      <div className="px-5">
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5">
          <p className="text-xs leading-relaxed text-gray-500">{t("expert.feuille.note")}</p>
        </div>
      </div>
    </div>
  );
}

/** The two doors, as the page needs them; the token is the caller's and never stored here. */
export interface SheetDoors {
  readonly read: (token: string) => Promise<SheetAnswer>;
  readonly acknowledge: (token: string, version: number) => Promise<{ acknowledgedAt: string }>;
  /** Whether a failure carries the DOOR's sentence (an envelope), as opposed to the network's. */
  readonly isDoorWords: (failure: unknown) => boolean;
}

/** A read of `live` AFTER an await, which the linter cannot see change. */
const isLive = (run: { readonly live: boolean }): boolean => run.live;

/**
 * The refusal's sentence: the door's own when the door answered (`isDoorWords`
 * says so), the catalogue's for anything else , a network failure must not
 * reach the cabinet as the browser's English.
 */
const refusalWords = (failure: unknown, isDoorWords: (failure: unknown) => boolean): string =>
  isDoorWords(failure) && failure instanceof Error ? failure.message : t("common.error.connexionEchouee");

/**
 * The page: reads the sheet by its token, then acknowledges the version it
 * shows , at the opening, once , and renders what the doors answered.
 */
export function TourSheetScreen(props: {
  readonly token: string;
  readonly doors: SheetDoors;
}): React.JSX.Element {
  const [state, setState] = useState<SheetState>({ kind: "loading" });
  const { token, doors } = props;

  useEffect(() => {
    const run = { live: true };
    void (async () => {
      let sheet: SheetAnswer;
      try {
        sheet = await doors.read(token);
      } catch (failure) {
        if (run.live) setState({ kind: "refused", message: refusalWords(failure, doors.isDoorWords) });
        return;
      }
      if (!run.live) return;
      setState({ kind: "sheet", sheet, acknowledgedAt: sheet.acknowledgedAt });
      if (sheet.acknowledgedAt !== null) return;
      // WF-CON-32 step 5: posted at the opening, with the version this page shows.
      try {
        const { acknowledgedAt } = await doors.acknowledge(token, sheet.version);
        if (isLive(run)) setState({ kind: "sheet", sheet, acknowledgedAt });
      } catch (failure) {
        if (isLive(run)) setState({ kind: "refused", message: refusalWords(failure, doors.isDoorWords) });
      }
    })();
    return () => {
      run.live = false;
    };
  }, [token, doors]);

  return <SheetPage state={state} />;
}
